import { useEffect, useState } from "react";
import { Briefcase, Users, Rss, Clock } from "lucide-react";
import { EmployeeDashboardStats, getEmployeeDashboardStats } from "features/dashboard/api/DashboardApi";
import { StatCard, StatusBadgeRow, AlertBanner, JOB_PIPELINE_KEYS } from "features/dashboard/ui/shared";

function Spinner() {
  return <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />;
}

export default function TmDashboard() {
  const [stats, setStats] = useState<EmployeeDashboardStats | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getEmployeeDashboardStats()
      .then(setStats)
      .catch((e) => setError(e.response?.data?.detail || "Failed to load dashboard stats"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10">
        <Spinner />
        <span className="text-sm text-muted-foreground font-inter">Loading dashboard…</span>
      </div>
    );
  }

  if (error || !stats) {
    return <p className="text-sm text-destructive font-inter">{error || "No data available."}</p>;
  }

  const todayTotal = stats.today.linkedin_alerts + stats.today.theirstack_jobs + stats.today.watchlist_leads;
  const newTotal =
    (stats.status_breakdown.linkedin_alerts.new ?? 0) +
    (stats.status_breakdown.theirstack_jobs.new ?? 0) +
    (stats.status_breakdown.watchlist_leads.new ?? 0);
  const alertRowCount =
    (newTotal > 0 ? 1 : 0) +
    (stats.hot_lead_stale_count > 0 ? 1 : 0) +
    (stats.stale_new_count > 0 ? 1 : 0) +
    (stats.stale_hiring_contacts_count > 0 ? 1 : 0);

  return (
    <div className="space-y-8">
      {/* Alerts */}
      {(newTotal > 0 || stats.stale_new_count > 0 || stats.hot_lead_stale_count > 0 || stats.stale_hiring_contacts_count > 0) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-bold text-foreground font-inter">Needs Attention</h2>
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold font-inter">
              {alertRowCount}
            </span>
          </div>
          <div className="space-y-2">
            {newTotal > 0 && (
              <AlertBanner to="/employee/job-board" linkLabel="Review Jobs" variant="action">
                You have <span className="font-semibold">{newTotal}</span> job{newTotal === 1 ? "" : "s"} in New status waiting for review.
              </AlertBanner>
            )}
            {stats.hot_lead_stale_count > 0 && (
              <AlertBanner to="/employee/job-board" linkLabel="Review Jobs" variant="urgent">
                <span className="font-semibold">{stats.hot_lead_stale_count}</span> active job{stats.hot_lead_stale_count === 1 ? "" : "s"} (interviewing/offer stage) untouched for {stats.hot_lead_stale_days}+ days — don't let these go cold.
              </AlertBanner>
            )}
            {stats.stale_new_count > 0 && (
              <AlertBanner to="/employee/job-board" linkLabel="Review Jobs" variant="urgent">
                <span className="font-semibold">{stats.stale_new_count}</span> of your jobs have been untouched for {stats.stale_new_days}+ days.
              </AlertBanner>
            )}
            {stats.stale_hiring_contacts_count > 0 && (
              <AlertBanner to="/employee/hiring-contacts" linkLabel="Follow Up" variant="urgent">
                <span className="font-semibold">{stats.stale_hiring_contacts_count}</span> Hiring Contact/Compan{stats.stale_hiring_contacts_count === 1 ? "y" : "ies"} assigned to you with no follow-up in {stats.stale_hiring_contacts_days}+ days.
              </AlertBanner>
            )}
          </div>
        </div>
      )}

      {/* Today's new jobs */}
      <div>
        <h2 className="text-base font-bold text-foreground font-inter mb-3">Today — New Jobs For You</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Clock} label="Total Today" value={todayTotal} hero />
          <StatCard icon={Briefcase} label="LinkedIn Alerts" value={stats.today.linkedin_alerts} />
          <StatCard icon={Rss} label="Other Jobs" value={stats.today.theirstack_jobs} />
          <StatCard icon={Users} label="Watchlist Leads" value={stats.today.watchlist_leads} />
        </div>
      </div>

      {/* My status breakdown */}
      <div>
        <h2 className="text-base font-bold text-foreground font-inter mb-3">My Job Status</h2>
        <div className="rounded-2xl border border-border bg-surface px-4">
          {JOB_PIPELINE_KEYS.map((key) => (
            <StatusBadgeRow key={key} pipelineKey={key} counts={stats.status_breakdown[key]} />
          ))}
        </div>
      </div>

      {/* My hiring contacts */}
      {(Object.keys(stats.hiring_contacts.companies).length > 0 || Object.keys(stats.hiring_contacts.contacts).length > 0) && (
        <div>
          <h2 className="text-base font-bold text-foreground font-inter mb-3">My Hiring Contacts</h2>
          <div className="rounded-2xl border border-border bg-surface px-4">
            <StatusBadgeRow pipelineKey="companies" counts={stats.hiring_contacts.companies} />
            <StatusBadgeRow pipelineKey="contacts" counts={stats.hiring_contacts.contacts} />
          </div>
        </div>
      )}
    </div>
  );
}
